import React, { useState, useRef } from 'react';
import AdminShell from './AdminShell';
import { usePortfolio } from '../../context/PortfolioContext';

const AdminPortfolio = () => {
    const {
        projects,
        isLoading: portfolioLoading,
        error: portfolioError,
        storageMode,
        uploadImage,
        addProject,
        updateProject,
        deleteProject,
        reorderProject,
        resetToDefaults
    } = usePortfolio();
    const fileInputRef = useRef(null);

    const [editingProject, setEditingProject] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // Form state for add/edit
    const [formData, setFormData] = useState({
        title: '',
        client: '',
        year: '',
        category: 'ready-to-wear',
        description: '',
        images: [],
        featured: false
    });

    const resetForm = () => {
        setFormData({
            title: '',
            client: '',
            year: '',
            category: 'ready-to-wear',
            description: '',
            images: [],
            featured: false
        });
        setUploadError('');
    };

    const handleEdit = (project) => {
        setFormData({
            title: project.title,
            client: project.client || '',
            year: project.year || '',
            category: project.category,
            description: project.description || '',
            images: project.images || [],
            featured: project.featured
        });
        setEditingProject(project);
    };

    const handleSave = async () => {
        try {
            if (editingProject) {
                await updateProject(editingProject.id, formData);
                setEditingProject(null);
            } else {
                await addProject(formData);
                setShowAddModal(false);
            }
            resetForm();
        } catch (err) {
            setUploadError(err.message);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteProject(id);
            setShowDeleteConfirm(null);
        } catch (err) {
            setUploadError(err.message);
        }
    };

    // File upload handlers
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        processFiles(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    const processFiles = async (files) => {
        setUploadError('');
        
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        
        if (imageFiles.length === 0) {
            setUploadError('Please select image files only (JPG, PNG, etc.)');
            return;
        }

        // Check file sizes (max 5MB each)
        const oversizedFiles = imageFiles.filter(file => file.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setUploadError('Some images are too large. Maximum size is 5MB per image.');
            return;
        }

        setIsUploading(true);

        try {
            // Upload each file using the context's uploadImage function
            for (const file of imageFiles) {
                const imageUrl = await uploadImage(file);
                setFormData(prev => ({
                    ...prev,
                    images: [...prev.images, imageUrl]
                }));
            }
        } catch (err) {
            setUploadError(err.message || 'Error uploading images. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleMoveImage = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= formData.images.length) return;
        
        const newImages = [...formData.images];
        const [removed] = newImages.splice(index, 1);
        newImages.splice(newIndex, 0, removed);
        setFormData(prev => ({ ...prev, images: newImages }));
    };

    return (
        <AdminShell title="Portfolio Manager" subtitle="Manage portfolio projects" viewLink="/portfolio">
            {/* Error Banner */}
            {portfolioError && (
                <div className="bg-red-500/20 border-b border-red-500 px-4 py-3">
                    <div className="container mx-auto">
                        <p className="text-red-400 text-sm">{portfolioError}</p>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8">
                {portfolioLoading && (
                    <p className="text-gray-400 mb-8">Loading projects...</p>
                )}

                {/* Actions Bar */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                        <h2 className="font-poppins font-light text-2xl text-white">Portfolio Items</h2>
                        <p className="text-gray-400 text-sm mt-1">{projects.length} projects total</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={resetToDefaults}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                        >
                            Reset to Defaults
                        </button>
                        <button
                            onClick={() => { resetForm(); setShowAddModal(true); }}
                            className="px-6 py-2 bg-primary-red hover:bg-primary-dark-red text-white rounded-lg transition-colors font-semibold"
                        >
                            + Add New Project
                        </button>
                    </div>
                </div>

                {/* Projects List */}
                <div className="space-y-4">
                    {projects.map((project, index) => (
                        <div 
                            key={project.id}
                            className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                        >
                            <div className="flex items-start gap-4">
                                {/* Thumbnail */}
                                <div className="flex-shrink-0 w-24 h-32 bg-gray-700 rounded-lg overflow-hidden">
                                    {project.images && project.images[0] ? (
                                        <img 
                                            src={project.images[0]} 
                                            alt={project.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs text-center p-2">
                                            No image
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-grow min-w-0">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <h3 className="font-poppins font-semibold text-white text-lg">
                                                {project.title}
                                            </h3>
                                            <p className="text-gray-400 text-sm">
                                                {project.client && <span>{project.client}</span>}
                                                {project.client && project.year && <span> • </span>}
                                                {project.year && <span>{project.year}</span>}
                                            </p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    project.category === 'ready-to-wear'
                                                        ? 'bg-green-500/20 text-green-400'
                                                        : project.category === 'press'
                                                            ? 'bg-blue-500/20 text-blue-400'
                                                            : 'bg-purple-500/20 text-purple-400'
                                                }`}>
                                                    {project.category === 'ready-to-wear' ? 'READY TO WEAR' : project.category === 'press' ? 'RUNWAY' : project.category.toUpperCase()}
                                                </span>
                                                <span className="text-gray-500 text-xs">
                                                    {project.images?.length || 0} image(s)
                                                </span>
                                                {project.featured && (
                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                                        FEATURED
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Reorder buttons */}
                                            <div className="flex flex-col gap-1">
                                                <button
                                                    onClick={() => reorderProject(project.id, 'up')}
                                                    disabled={index === 0}
                                                    className={`p-1 rounded ${
                                                        index === 0 
                                                            ? 'text-gray-600 cursor-not-allowed' 
                                                            : 'text-gray-400 hover:text-white hover:bg-gray-700'
                                                    }`}
                                                    title="Move up"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => reorderProject(project.id, 'down')}
                                                    disabled={index === projects.length - 1}
                                                    className={`p-1 rounded ${
                                                        index === projects.length - 1 
                                                            ? 'text-gray-600 cursor-not-allowed' 
                                                            : 'text-gray-400 hover:text-white hover:bg-gray-700'
                                                    }`}
                                                    title="Move down"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            </div>

                                            <button
                                                onClick={() => handleEdit(project)}
                                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => setShowDeleteConfirm(project.id)}
                                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Description preview */}
                                    {project.description && (
                                        <p className="text-gray-500 text-sm mt-3 line-clamp-2">
                                            {project.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {projects.length === 0 && (
                    <div className="text-center py-16">
                        <p className="text-gray-500">No portfolio items yet.</p>
                        <button
                            onClick={() => { resetForm(); setShowAddModal(true); }}
                            className="mt-4 px-6 py-2 bg-primary-red hover:bg-primary-dark-red text-white rounded-lg transition-colors"
                        >
                            Add Your First Project
                        </button>
                    </div>
                )}
            </main>

            {/* Add/Edit Modal */}
            {(showAddModal || editingProject) && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 flex items-start justify-center p-4 pt-10">
                    <div className="bg-gray-800 rounded-2xl w-full max-w-2xl border border-gray-700 shadow-2xl">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <h3 className="font-poppins font-semibold text-xl text-white">
                                {editingProject ? 'Edit Project' : 'Add New Project'}
                            </h3>
                            <button
                                onClick={() => { setShowAddModal(false); setEditingProject(null); resetForm(); }}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Title - Required */}
                            <div>
                                <label className="block text-gray-300 text-sm font-medium mb-2">
                                    Title / Client Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red text-lg"
                                    placeholder="e.g. Burberry AW24 or just Burberry"
                                    required
                                />
                                <p className="text-gray-500 text-xs mt-1">This is the only required field</p>
                            </div>

                            {/* Optional Fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Client <span className="text-gray-500">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.client}
                                        onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red"
                                        placeholder="e.g. Burberry"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Year <span className="text-gray-500">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.year}
                                        onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red"
                                        placeholder="e.g. 2024"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">Category</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red"
                                    >
                                        <option value="ready-to-wear">Ready to Wear</option>
                                        <option value="press">Runway</option>
                                        <option value="merchandise">Merchandise</option>
                                    </select>
                                </div>
                                <div className="flex items-end pb-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.featured}
                                            onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                                            className="w-5 h-5 rounded border-gray-600 text-primary-red focus:ring-primary-red bg-gray-700"
                                        />
                                        <span className="text-gray-300">Featured project</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-300 text-sm font-medium mb-2">
                                    Description <span className="text-gray-500">(optional)</span>
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red resize-none"
                                    rows={3}
                                    placeholder="Project description..."
                                />
                            </div>

                            {/* Image Upload Section */}
                            <div>
                                <label className="block text-gray-300 text-sm font-medium mb-2">
                                    Images ({formData.images.length})
                                    {storageMode === 'api' && (
                                        <span className="text-green-400 text-xs ml-2">
                                            ✓ Images will be saved to server
                                        </span>
                                    )}
                                </label>
                                
                                {/* Drag & Drop Zone */}
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => !isUploading && fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                        isUploading 
                                            ? 'border-gray-600 bg-gray-700/50 cursor-wait'
                                            : isDragging 
                                                ? 'border-primary-red bg-primary-red/10 cursor-pointer' 
                                                : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50 cursor-pointer'
                                    }`}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        disabled={isUploading}
                                    />
                                    {isUploading ? (
                                        <>
                                            <svg className="w-12 h-12 mx-auto mb-4 text-primary-red animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <p className="text-gray-300">Uploading images...</p>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-12 h-12 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <p className="text-gray-300 mb-2">
                                                <span className="text-primary-red font-medium">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-gray-500 text-sm">PNG, JPG, GIF up to 5MB each</p>
                                        </>
                                    )}
                                </div>

                                {/* Upload Error */}
                                {uploadError && (
                                    <p className="text-red-400 text-sm mt-2">{uploadError}</p>
                                )}

                                {/* Image list */}
                                {formData.images.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {formData.images.map((img, index) => (
                                            <div key={index} className="flex items-center gap-3 bg-gray-700/50 rounded-lg p-2">
                                                <div className="w-16 h-16 bg-gray-600 rounded overflow-hidden flex-shrink-0">
                                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                                </div>
                                                <div className="flex-grow min-w-0">
                                                    <p className="text-gray-300 text-sm">Image {index + 1}</p>
                                                    <p className="text-gray-500 text-xs truncate">
                                                        {img.startsWith('data:') ? 'Base64 (prototype)' : img}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveImage(index, 'up')}
                                                        disabled={index === 0}
                                                        className={`p-1.5 rounded ${index === 0 ? 'text-gray-600' : 'text-gray-400 hover:text-white hover:bg-gray-600'}`}
                                                        title="Move up"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveImage(index, 'down')}
                                                        disabled={index === formData.images.length - 1}
                                                        className={`p-1.5 rounded ${index === formData.images.length - 1 ? 'text-gray-600' : 'text-gray-400 hover:text-white hover:bg-gray-600'}`}
                                                        title="Move down"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(index)}
                                                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded"
                                                        title="Remove"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
                            <button
                                onClick={() => { setShowAddModal(false); setEditingProject(null); resetForm(); }}
                                className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={!formData.title.trim() || isUploading}
                                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                                    !formData.title.trim() || isUploading
                                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                        : 'bg-primary-red hover:bg-primary-dark-red text-white'
                                }`}
                            >
                                {editingProject ? 'Save Changes' : 'Add Project'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 flex items-center justify-center p-4">
                    <div className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl p-6">
                        <h3 className="font-poppins font-semibold text-xl text-white mb-4">
                            Delete Project?
                        </h3>
                        <p className="text-gray-400 mb-6">
                            Are you sure you want to delete this project? This action cannot be undone.
                        </p>
                        <div className="flex items-center justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm)}
                                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminShell>
    );
};

export default AdminPortfolio;
